import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useSendBridgetMessageMutation } from '@common/cache';
import { BridgetChatEntry } from '../types';
import { toast } from 'react-toastify';
import { entries } from 'lodash';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { t } from 'i18next';
import { Opts as LinkifyOptions } from 'linkifyjs';
import { Link } from '@mui/material';

export const useBridget = () => {
  const [isBridgetOpen, setIsBridgetOpen] = useState(false);
  const [draftMessage, setDraftMessage] = useState('');
  const bridgetMutation = useSendBridgetMessageMutation();
  const [chatEntries, setChatEntries] = useState<BridgetChatEntry[]>([]);
  const chatEndRef = useRef<Element>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const maxMessageLength = 300;

  function toBaseDomain(url: string) {
    try {
      const u = new URL(url.startsWith('http') ? url : `https://${url}`);
      const parts = u.hostname.split('.');
      if (parts.length <= 2) return u.hostname;
      return parts.slice(-2).join('.');
    } catch {
      return url;
    }
  }

  const linkifyOptions = {
    defaultProtocol: 'https',
    render: {
      url: ({ attributes, content }) => (
        <Link
          href={attributes.href}
          target='_blank'
          rel='noopener noreferrer'
          sx={{
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          {toBaseDomain(content)}
        </Link>
      ),
    },
  } as LinkifyOptions;

  useEffect(() => {
    if (isBridgetOpen && chatEntries.length === 0) {
      setChatEntries([
        {
          message: t('bridget.greeting'),
          isOwnMessage: false,
          time: new Date(),
        },
      ]);
    }
  }, [isBridgetOpen, chatEntries.length]);

  const handleCloseBridget = () => {
    setIsBridgetOpen(false);
  };

  const handleToggleBridget = () => {
    setIsBridgetOpen(prevState => !prevState);
  };

  const handleChangeDraftMessage = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setDraftMessage(event.target.value);
  };

  const isBridgetTyping = bridgetMutation.isPending;
  const isOverLimit = draftMessage.length > maxMessageLength;
  const isSendDisabled = !draftMessage.trim() || isBridgetTyping || isOverLimit;

  const handleSendMessage = async () => {
    try {
      setChatEntries(prev => [
        ...prev,
        { message: draftMessage, isOwnMessage: true, time: new Date() },
      ]);

      const response = await bridgetMutation.mutateAsync({
        message: draftMessage,
      });

      setChatEntries(prev => [
        ...prev,
        {
          message: response.data,
          isOwnMessage: false,
          time: new Date(),
        },
      ]);
      setDraftMessage('');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleOnTextfieldKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
  ) => {
    if (event.key === 'Enter') {
      if (event.shiftKey) {
        setDraftMessage(draftMessage + '\n');
      } else {
        if (draftMessage.length > maxMessageLength) {
          event.preventDefault();
          return;
        }
        event.preventDefault();
        if (draftMessage.trim()) {
          handleSendMessage();
          setDraftMessage('');
        }
      }
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 100);

    return () => clearTimeout(timeout);
  }, [draftMessage, entries.length, bridgetMutation.isSuccess, isBridgetOpen]);

  useEffect(() => {
    if (!isBridgetTyping) {
      inputRef.current?.focus();
    }
  }, [isBridgetTyping]);

  return {
    isBridgetOpen,
    isSendDisabled,
    isBridgetTyping,
    handleCloseBridget,
    handleToggleBridget,
    handleSendMessage,
    handleChangeDraftMessage,
    chatEntries,
    handleOnTextfieldKeyDown,
    draftMessage,
    chatEndRef,
    maxMessageLength,
    linkifyOptions,
    inputRef,
    helperTextMessage:
      draftMessage.length > 0
        ? `${draftMessage.length}/${maxMessageLength}`
        : '',
  };
};
